import React, { useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import type { FilterableSelectorOption } from '@this/domain/filterable_selector_option';

interface Props {
  placeholder: string;
  options: FilterableSelectorOption[];
  selected: FilterableSelectorOption | null;
  onSelect: (option: FilterableSelectorOption | null) => void;
  onChange?: (query: string) => void;
  isRequired?: boolean;
  workflowFormWidth: string;
}

interface HTMLElementEvent<T extends EventTarget> extends Event {
  target: T;
}

const FilterableSelector: React.FC<Props> = ({
  placeholder,
  options,
  selected,
  onSelect,
  onChange,
  isRequired,
  workflowFormWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectAreaRef = useRef<HTMLDivElement>(null);

  const handleClickEvent = (e: HTMLElementEvent<HTMLElement>) => {
    if (selectAreaRef.current && !selectAreaRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const clickHandler = {
      handleEvent: (e: HTMLElementEvent<HTMLElement>) => {
        handleClickEvent(e);
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, []);

  const getFilteredOptions = () => {
    return options.filter(o => {
      const matcher = o.matcher.toUpperCase();
      const queryUpper = query.toUpperCase();
      return matcher.indexOf(queryUpper) > -1;
    });
  };

  const toggleOpen = () => {
    setIsOpen(prev => {
      const newIsOpen = !prev;
      if (newIsOpen) {
        setTimeout(() => {
          const el = document.getElementById('filterable-selector-input');
          if (el) {
            el.focus();
          }
        }, 0);
      }
      return newIsOpen;
    });
  };

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    if (onChange) {
      onChange(newQuery);
    }
  };

  const handleSelect = (o: FilterableSelectorOption | null) => {
    setIsOpen(false);
    onSelect(o);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    }
  };

  const filteredOptions = getFilteredOptions();
  const unselectedLabel = _.isNil(isRequired)
    ? '選択してください'
    : isRequired
    ? '(必須)選択してください'
    : '(任意)選択してください';

  const grayBorderColor = '#eee';
  const fieldBgColor = '#fff';

  return (
    <div
      ref={selectAreaRef}
      style={{ width: '100%', fontSize: '13px', marginBottom: '5px', lineHeight: 'normal' }}
    >
      <div
        data-testid="fs_open_box"
        role="button"
        tabIndex={0}
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        style={{
          display: 'flex',
          border: `2px solid ${grayBorderColor}`,
          padding: '2px 10px',
          cursor: 'pointer',
          lineHeight: 'normal',
          whiteSpace: 'normal',
          wordBreak: 'break-all'
        }}
      >
        <div>{selected && selected.label !== '' ? selected.label : unselectedLabel}</div>
        <div style={{ marginLeft: 'auto' }}>{isOpen ? '▲' : '▼'}</div>
      </div>
      {isOpen && (
        <div style={{ margin: 0, background: fieldBgColor }}>
          <div style={{ marginTop: '5px' }}>
            <input
              type="text"
              id="filterable-selector-input"
              placeholder={placeholder}
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              style={{ marginBottom: 0 }}
            />
          </div>
          <ul
            style={{
              overflowY: 'scroll',
              maxHeight: '150px',
              width: workflowFormWidth || '420px',
              borderBottom: `2px solid ${grayBorderColor}`
            }}
          >
            <li
              role="option"
              aria-selected={selected === null}
              tabIndex={0}
              onClick={() => handleSelect(null)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(null);
                }
              }}
              style={{
                background: fieldBgColor,
                minHeight: '30px',
                width: '100%',
                lineHeight: '30px',
                padding: '0 10px',
                cursor: 'pointer',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                border: `2px solid ${grayBorderColor}`,
                borderTop: 'none',
                zIndex: 100
              }}
            >
              選択を外す
            </li>
            {filteredOptions.map(o => (
              <li
                data-wovn-ignore
                key={o.value}
                role="option"
                aria-selected={selected?.value === o.value}
                tabIndex={0}
                onClick={() => handleSelect(o)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(o);
                  }
                }}
                style={{
                  background: fieldBgColor,
                  minHeight: '30px',
                  width: '100%',
                  lineHeight: '30px',
                  padding: '0 10px',
                  cursor: 'pointer',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  border: `2px solid ${grayBorderColor}`,
                  borderTop: 'none',
                  zIndex: 100
                }}
              >
                {o.label}
                {o.labelLines?.map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FilterableSelector;
